import type { FC, ReactNode } from 'react'

import {
    Fragment,
    createElement,
    useEffect,
    useRef,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface RevealTextProps {
    lines: Array<string>
    as?: 'h1' | 'h2' | 'h3' | 'p'
    className?: string
    delay?: number
    stagger?: number
    immediate?: boolean
}

// Slides each word up from behind a mask, line by line. Screen readers get the plain sentence.
export const RevealText: FC<RevealTextProps> = ({
    lines,
    as = 'h2',
    className,
    delay = 0,
    stagger = 60,
    immediate = false
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const inView = useInView(ref)
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        if (!immediate) return

        const frame = requestAnimationFrame(() => setMounted(true))

        return () => cancelAnimationFrame(frame)
    }, [immediate])

    const play = immediate ? mounted : inView

    let order = 0

    const animated = lines.map((line, lineIndex) => {
        const words = line.split(' ')

        return (
            <span key={`${line}-${lineIndex}`} className='block'>
                {words.map((word, wordIndex) => {
                    const wordDelay = delay + order * stagger

                    order += 1

                    return (
                        <Fragment key={`${word}-${wordIndex}`}>
                            <span className='-mt-[0.08em] -mb-[0.16em] inline-block overflow-hidden pt-[0.08em] pb-[0.16em] align-top'>
                                <span
                                    className={cn('split-word inline-block', play && 'is-visible')}
                                    style={{ transitionDelay: `${wordDelay}ms` }}
                                >
                                    {word}
                                </span>
                            </span>
                            {wordIndex < words.length - 1 ? ' ' : null}
                        </Fragment>
                    )
                })}
            </span>
        )
    })

    return createElement(
        as,
        { ref, className },
        <span className='sr-only'>{lines.join(' ')}</span>,
        <span aria-hidden='true' className='block'>{animated}</span>
    )
}
